"use client";

import { useState } from "react";
import { useForm } from "react-hook-form";
import { Box, Typography } from "@mui/material";
import { validationRules } from "@/common/components/SalesFlow/SalesFlowRegistrationForm/SalesFlowRegistrationForm.utils";
import CtaButton from "@/common/components/CtaButton/CtaButton";
import CheckboxField from "@/common/components/FormController/Fields/CheckboxField/CheckboxField";
import {
  formButtonStyles,
  formFieldSetStyles,
  styledInvalidVoucherWarning,
} from "@/common/components/SalesFlow/SalesFlowRegistrationForm/SalesFlowRegistrationForm.styles";
import { styledStepTitle } from "@/common/components/StepTitle/StepTitle.styled";
import { MergedClub } from "@/common/types/api.interfaces";
import { BaseFormField } from "@/common/utils/formFields";
import { useExtensionFlow } from "@/widgets/ExtensionFlow/context/ExtensionFlowContext";
import { useUpdateQueryParams } from "@/widgets/BecomeAMember/hooks/useUpdateQueryParams";
import { Languages, UrlParamKeys } from "@/common/utils/constants";
import { Maybe } from "@/generated/contentfulTypes";
import { useNativeFormValidator } from "@/common/hooks/useNativeFormValidator";
import { handleMembershipExtension } from "./ExtensionTermsAndCondition.service";
import { Sizes, Variants } from "@/common/components/CtaButton/CtaButton.interfaces";

const DEFAULT_TERMS_AND_CONDITIONS_VALUES = {
  [BaseFormField.TermsAndConditions]: false,
  [BaseFormField.ReceiveNewsletter]: false,
};

export interface ExtensionTermsAndCondionProps {
  translations: Record<string, string>;
  isInvalidVoucher?: boolean;
  isSubmitButtonHidden?: boolean;
  skipPayment?: boolean;
  lang: Languages;
  club?: Maybe<MergedClub>;
  onRegisterValidator?: (validator: () => Promise<boolean>) => void;
}

export default function ExtensionTermsAndCondition({
  translations,
  isInvalidVoucher,
  isSubmitButtonHidden = false,
  skipPayment = false,
  lang,
  club,
  onRegisterValidator,
}: ExtensionTermsAndCondionProps) {
  const { dataLayerData, customerExtensionEndDate, cancelationReasonId, setIsPaymentWidgetVisible, additionalInformationId, additionalInformationValue } = useExtensionFlow();
  const { searchParams } = useUpdateQueryParams();
  const [isLoading, setIsLoading] = useState(false);
  const [submittedRateBundleTermId, setSubmittedRateBundleTermId] = useState<string | null>(null);

  const rateBundleTermId = searchParams.get(UrlParamKeys.RateBundleTermId);

  const { handleSubmit, control } = useForm({
    defaultValues: DEFAULT_TERMS_AND_CONDITIONS_VALUES,
    mode: "onBlur",
    reValidateMode: "onChange",
  });

  const onSubmit = async (formData: { termsAndConditions: boolean; receiveNewsletter: boolean }) => {
    try {
      setIsLoading(true);
      setSubmittedRateBundleTermId(rateBundleTermId);

      if (skipPayment && club) {
        const onError = () => setIsPaymentWidgetVisible(false);
        await handleMembershipExtension({
          club,
          dataLayerData,
          customerExtensionEndDate,
          cancelationReasonId,
          onError,
          searchParams,
          formData,
          lang,
          additionalInformationId,
          additionalInformationValue
        });
      }
    } catch (error) {
      console.error(error);
      setIsLoading(false);
      setSubmittedRateBundleTermId(null);
    }
  };

  const { formRef } = useNativeFormValidator({
    mode: "submit",
    handleSubmit,
    onSubmit,
    onRegisterValidator,
  })

  const isRateBundleMissing = !skipPayment && !rateBundleTermId;
  const isFormSubmitting = isLoading && submittedRateBundleTermId === rateBundleTermId;

  const isSubmitButtonDisabled =
    isRateBundleMissing || isFormSubmitting || isInvalidVoucher;

  return (
    <>
      <Typography sx={styledStepTitle}>
        {translations["extensionflow.tandc.title"]}
      </Typography>
      <form ref={formRef} onSubmit={handleSubmit(onSubmit)}>
        <Box sx={formFieldSetStyles} component="fieldset">
          <CheckboxField
            required
            rules={validationRules(translations).required}
            name={BaseFormField.TermsAndConditions}
            control={control}
            openLinksInNewTab
            label={translations["extensionflow.tandc"]}
          />
          <CheckboxField
            name={BaseFormField.ReceiveNewsletter}
            control={control}
            openLinksInNewTab
            label={translations["extensionflow.newsletter"]}
          />
          {isInvalidVoucher && (
            <Typography sx={styledInvalidVoucherWarning}>
              {translations["extensionflow.invalidvoucher"]}
            </Typography>
          )}
          {!isSubmitButtonHidden && skipPayment && (
            <Box sx={formButtonStyles}>
              <CtaButton
                variant={Variants.Red}
                size={Sizes.Large}
                fullWidth
                type="submit"
                disabled={isSubmitButtonDisabled}
              >
                {translations["extensionflow.submitbutton"]}
              </CtaButton>
            </Box>
          )}
        </Box>
      </form>
    </>
  );
}
